<template>
  <div class="tag-picker">
    <h2 class="tag-picker__heading heading--4">
      Categories
    </h2>
    <p class="tag-picker__instructions">
      {{ promptForTagSelection }}
    </p>
    <search-form
      id="tagPickerSearch"
      :term="search"
      placeholder="Search categories"
      aria-label="Search categories"
      :wrapper-attrs="{ role: 'searchbox' }"
      :is-form="false"
      data-test-id="tag-picker-search-form"
      @input="searchTags"
    />
    <ol class="tag-picker__tag-list inline-list">
      <li
        v-for="tag in filteredTags"
        :key="`tag-${tag.id}`"
      >
        <button
          type="button"
          class="tag-picker__tag"
          :class="{ 'tag-picker__tag--selected': tagIsSelected(tag) }"
          data-test-id="tag-picker-tag"
          @click="selectTag(tag)"
        >
          {{ tag.name }}
        </button>
      </li>
    </ol>
    <p
      v-if="filteredTags.length === 0"
      class="tag-picker__no-tags"
    >
      Sorry, we couldn’t find any categories that match.
    </p>
    <button
      v-if="showTruncationButton"
      class="tag-picker__expand-button"
      @click="expandTruncation"
    >Show {{ truncatedTagCount }} more</button>
  </div>
</template>

<script>
import SearchForm from '@/components/Search/SearchForm.vue';
import { isDesktop } from '@/utils/viewport-width-helpers';
import { mapGetters } from 'vuex';

export default {
  name: 'TagPicker',
  components: {
    SearchForm,
  },
  props: {
    forumTags: {
      type: Array,
      required: true,
    },
    truncation: {
      type: Boolean,
      default: false,
    },
    withinForm: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: '',
      limit: 3,
      truncationLimit: 8,
      expanded: false,
      isDesktop: false,
    };
  },
  computed: {
    ...mapGetters(['selectedTags']),
    truncatedTagCount() {
      if (!this.forumTags) { return 0; }
      return this.forumTags.length - this.filteredTags.length;
    },
    filteredTags() {
      if (!this.forumTags) { return []; }
      if (!this.search) { return this.truncateTags(this.forumTags); }
      const filtered = this.forumTags.filter(
        (tag) => tag.name.toLowerCase().includes(this.search.toLowerCase()),
      );
      return this.truncateTags(filtered);
    },
    promptForTagSelection() {
      if (this.withinForm) {
        return 'Select a category below to help other community members find your discussion';
      }
      return 'Select a category to find topics that interest you.';
    },
    showTruncationButton() {
      return this.showTruncated && this.truncatedTagCount > 0;
    },
    showTruncated() {
      return this.truncation && !this.expanded && !this.isDesktop;
    },
  },
  mounted() {
    this.isDesktop = isDesktop();
  },
  methods: {
    expandTruncation() {
      this.expanded = true;
    },
    truncateTags(tags) {
      if (!this.showTruncated) {
        return tags;
      }
      return tags.slice(0, this.truncationLimit);
    },
    searchTags(query) {
      this.search = query;
    },
    selectTag(tag) {
      let action = 'setSelectedTags';
      const newTags = this.selectedTags;

      if (this.selectedTags.length >= this.limit && !newTags.includes(tag.id)) {
        this.$store.dispatch('addToastNotification', {
          toastType: 'error',
          description: `You may only select up to ${this.limit} tags`,
          type: 'Forum',
        });
        return;
      }

      if (newTags.includes(tag.id)) {
        action = 'removeSelectedTag';
        newTags.splice(newTags.indexOf(tag.id), 1);
      }

      newTags.push(tag.id);
      this.$store.dispatch(action, newTags);
      this.$emit('tagsSelected', newTags);
    },
    tagIsSelected(tag) {
      return this.selectedTags.includes(tag.id);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '@/stylesheets/components/_tag-picker';
</style>

<docs>

Can be displayed with a truncated list of tags

</docs>
